import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BoardHuePicker } from "./BoardHuePicker";
import { pastelOf } from "../../editor/pastel";
import type { ColorSlot } from "../../editor/colorSlots";

type Props = {
  /** Три пера (или три цвета стикера) под рукой. */
  slots: ColorSlot[];
  activeColor: string;
  /** Какая точка сейчас в руке. */
  activeIndex: number;
  /** Готовые цвета инструмента. */
  presets: readonly string[];
  /** Показывать под каждым цветом его пастельный сородич. */
  withPastel?: boolean;
  /** Цвета, которые человек добавил сам. */
  customColors: string[];
  /** Толщина настраивается только у пера. */
  withWidth?: boolean;
  /** Готовые толщины. */
  widthPresets?: readonly number[];
  /** Стиль линии: сплошная, пунктир, точки. */
  dashes?: readonly { id: string; label: string; title: string }[];
  /** Вид линии меняется у той точки, которую открыли, а не у всех сразу. */
  onPickDash?: (id: string, index: number) => void;
  /** Выбрать перо для рисования. */
  onPick: (slot: ColorSlot, index: number) => void;
  /** Изменить точку. */
  onSetSlot: (index: number, patch: Partial<ColorSlot>) => void;
  onAddCustom: (hex: string) => void;
  onRemoveCustom: (hex: string) => void;
  label?: string;
};

function sameColor(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

function swatchShadow(hex: string): string | undefined {
  return hex.toLowerCase() === "#ffffff" ? "inset 0 0 0 1px #cbd5e1" : undefined;
}

/**
 * Диаметр кружка внутри точки — по нему видно, чем будешь писать.
 *
 * Раньше было `12 + толщина × 0,85`: между пером в 1 и в 3 точки разница
 * выходила в две точки, то есть никакая, и кружок «не отражал размер».
 * Теперь разброс широкий — от 7 до 30 точек на толщинах от 1 до 16.
 */
function dotSize(width: number, withWidth: boolean): number {
  if (!withWidth) return 22;
  const w = Math.min(24, Math.max(1, width));
  // Потолок опущен с 30 до 24: вокруг кружка теперь идёт кольцо, которое
  // показывает вид линии, и самому большому перу нужно место под него.
  return Math.round(Math.min(24, 5 + w * 1.3));
}

/** Подпись вида линии — для всплывающей подсказки у точки. */
const DASH_WORD: Record<string, string> = {
  solid: "сплошная",
  dashed: "пунктир",
  dotted: "точки",
};

/** Сама кнопка-точка размера не меняет: иначе строка «дышит» при выборе. */
const SLOT_BUTTON_PX = 34;

/**
 * Три настраиваемые точки: цвет и толщина у каждой своя.
 *
 * Нажатие по точке и берёт это перо, и открывает маленькое окошко рядом —
 * цвета и толщина там же. Окошко висит поверх всего (портал), а не внутри
 * панели инструмента: внутри оно раздвигало панель, и цвет выбирался долго.
 */
export function BoardColorSlots({
  slots,
  activeColor,
  activeIndex,
  presets,
  withPastel = false,
  customColors,
  withWidth = false,
  widthPresets = [1, 2, 3, 5, 8, 12, 20],
  dashes,
  onPickDash,
  onPick,
  onSetSlot,
  onAddCustom,
  onRemoveCustom,
  label = "Цвет",
}: Props) {
  const [openSlot, setOpenSlot] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState(activeColor);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // Верх окошка по настоящей высоте: с открытым «Свой цвет» оно почти вдвое
  // выше, и прежний расчёт «под 320 точек» уводил низ с кнопкой «Добавить»
  // за край экрана — казалось, что палитра пропала.
  const [popTop, setPopTop] = useState<number | null>(null);

  useEffect(() => {
    if (openSlot === null) return;
    function onDown(ev: Event) {
      const t = ev.target as Node | null;
      if (popRef.current?.contains(t as Node)) return;
      if (rowRef.current?.contains(t as Node)) return;
      if (t instanceof Element && t.closest(".wb-tour")) return;
      setOpenSlot(null);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") setOpenSlot(null);
    }
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [openSlot]);

  useEffect(() => {
    if (openSlot === null) setPickerOpen(false);
  }, [openSlot]);

  useLayoutEffect(() => {
    const el = popRef.current;
    if (openSlot === null || !anchor || !el) {
      setPopTop(null);
      return;
    }
    function place() {
      if (!el || !anchor) return;
      const viewH = window.visualViewport?.height ?? window.innerHeight;
      const h = el.offsetHeight;
      setPopTop(Math.max(8, Math.min(anchor.y, viewH - h - 8)));
    }
    place();
    // Картинки и шрифты могут дорисоваться позже — следим за размером.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    ro?.observe(el);
    window.addEventListener("resize", place);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [openSlot, anchor, pickerOpen]);

  const index = openSlot ?? 0;
  const slot = slots[index] ?? { color: activeColor, width: 3, dash: "solid" as const };

  function setColor(hex: string) {
    onSetSlot(index, { color: hex });
    onPick({ color: hex, width: slot.width, dash: slot.dash }, index);
  }

  function setWidth(w: number) {
    onSetSlot(index, { width: w });
    onPick({ color: slot.color, width: w, dash: slot.dash }, index);
  }

  return (
    <div className="wb-color-slots">
      <div className="wb-color-slots__row" role="group" aria-label={label} ref={rowRef}>
        {slots.map((s, i) => {
          const size = dotSize(s.width, withWidth);
          return (
            <button
              key={i}
              type="button"
              className={`wb-color-slots__dot${i === activeIndex ? " is-active" : ""}${
                openSlot === i ? " is-open" : ""
              }`}
              style={{ width: SLOT_BUTTON_PX, height: SLOT_BUTTON_PX }}
              title={
                withWidth
                  ? `Перо ${i + 1}: ${s.color}, толщина ${s.width}px, ${
                      DASH_WORD[s.dash] ?? "сплошная"
                    }`
                  : `Цвет ${i + 1}: ${s.color}`
              }
              aria-label={withWidth ? `Перо ${i + 1}` : `Цвет ${i + 1}`}
              aria-expanded={openSlot === i}
              onClick={(ev) => {
                onPick(s, i);
                setDraft(s.color);
                const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                setAnchor({ x: r.x + r.width / 2, y: r.bottom + 8 });
                setOpenSlot((prev) => (prev === i ? null : i));
              }}
            >
              {/* Кольцо — вид линии. Выбранную точку по-прежнему отмечает
                  фиолетовый контур снаружи, так что видно и то и другое. */}
              {withWidth ? (
                <span
                  className={`wb-color-slots__dot-ring is-${s.dash}`}
                  style={{ borderColor: s.color }}
                  aria-hidden="true"
                />
              ) : null}
              <span
                className="wb-color-slots__dot-ink"
                style={{
                  width: size,
                  height: size,
                  background: s.color,
                  boxShadow: swatchShadow(s.color),
                }}
              />
            </button>
          );
        })}
      </div>

      {openSlot !== null && anchor
        ? createPortal(
            <div
              ref={popRef}
              className="wb-color-pop"
              role="dialog"
              aria-label={withWidth ? `Перо ${index + 1}` : `Цвет ${index + 1}`}
              style={{
                left: Math.min(Math.max(8, anchor.x - 130), window.innerWidth - 268),
                top: popTop ?? anchor.y,
                // Пока не измерили — не показываем, чтобы не мигало внизу.
                visibility: popTop === null ? "hidden" : undefined,
              }}
            >
              <div className="wb-color-pop__grid">
                {presets.map((c) => {
                  // Пастель стоит СТОЛБИКОМ под своим цветом, а не отдельной
                  // строкой: строка переносится по ширине окошка, и пара
                  // разъезжалась — светлый оказывался под чужим цветом.
                  const soft = withPastel ? pastelOf(c) : null;
                  const base = (
                    <button
                      type="button"
                      className={`wb-color-pop__swatch${sameColor(c, slot.color) ? " is-active" : ""}`}
                      style={{ background: c, boxShadow: swatchShadow(c) }}
                      title={c}
                      onClick={() => setColor(c)}
                    />
                  );
                  if (!withPastel) return <span key={c}>{base}</span>;
                  return (
                    <span key={c} className="wb-color-pop__pair">
                      {base}
                      {soft ? (
                        <button
                          type="button"
                          className={`wb-color-pop__swatch${sameColor(soft, slot.color) ? " is-active" : ""}`}
                          style={{ background: soft, boxShadow: swatchShadow(soft) }}
                          title={`${soft} — пастельный`}
                          onClick={() => setColor(soft)}
                        />
                      ) : (
                        <span className="wb-color-pop__pair-gap" aria-hidden="true" />
                      )}
                    </span>
                  );
                })}
                {customColors.map((c) => (
                  <span key={c} className="wb-color-pop__custom">
                    <button
                      type="button"
                      className={`wb-color-pop__swatch${sameColor(c, slot.color) ? " is-active" : ""}`}
                      style={{ background: c, boxShadow: swatchShadow(c) }}
                      title={c}
                      onClick={() => setColor(c)}
                    />
                    <button
                      type="button"
                      className="wb-color-pop__remove"
                      title={`Убрать ${c} из своих`}
                      aria-label={`Убрать ${c} из своих`}
                      onClick={() => onRemoveCustom(c)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  className={`wb-color-pop__more${pickerOpen ? " is-active" : ""}`}
                  title="Свой цвет"
                  aria-label="Свой цвет"
                  aria-expanded={pickerOpen}
                  onClick={() => {
                    setDraft(slot.color);
                    setPickerOpen((v) => !v);
                  }}
                >
                  +
                </button>
              </div>

              {withWidth ? (
                <div className="wb-color-pop__widths" role="group" aria-label="Толщина">
                  {widthPresets.map((w) => (
                    <button
                      key={w}
                      type="button"
                      className={`wb-color-pop__width${slot.width === w ? " is-active" : ""}`}
                      title={`${w}px`}
                      aria-label={`Толщина ${w}`}
                      onClick={() => setWidth(w)}
                    >
                      <span
                        className="wb-color-pop__width-dot"
                        style={{
                          width: Math.min(22, w + 4),
                          height: Math.min(22, w + 4),
                          background: slot.color,
                        }}
                      />
                    </button>
                  ))}
                </div>
              ) : null}

              {dashes && dashes.length ? (
                <div className="wb-color-pop__dashes" role="group" aria-label="Стиль линии">
                  {dashes.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className={`wb-color-pop__dash${slot.dash === d.id ? " is-active" : ""}`}
                      title={d.title}
                      aria-label={d.title}
                      aria-pressed={slot.dash === d.id}
                      onClick={() => onPickDash?.(d.id, index)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {pickerOpen ? (
                <div className="wb-color-pop__picker">
                  <BoardHuePicker color={draft} onChange={setDraft} />
                  <div className="wb-color-pop__actions">
                    <button
                      type="button"
                      className="wb-btn wb-btn--primary wb-btn--sm"
                      // Фокус сюда: Enter добавляет цвет, а не нажимает «+» снова.
                      autoFocus
                      onClick={() => {
                        onAddCustom(draft);
                        setColor(draft);
                        setPickerOpen(false);
                      }}
                    >
                      Добавить
                    </button>
                    <button
                      type="button"
                      className="wb-btn wb-btn--ghost wb-btn--sm"
                      onClick={() => setPickerOpen(false)}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
